import { DialogButton, EpicIcons } from "@constellation-academy/epic-ui";
import { Button } from "primereact/button";
import { useCallback, useState } from "react";
import { useFragment, useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import type { createIhkCertificateRewardBundleButton_AddIHKCertificateRewardBundleMutation } from "@relay/createIhkCertificateRewardBundleButton_AddIHKCertificateRewardBundleMutation.graphql";
import type { createIhkCertificateRewardBundleButton_TreeNodeFragment$key } from "@relay/createIhkCertificateRewardBundleButton_TreeNodeFragment.graphql";
import {
	ADD_IHK_CERTIFICATE_REWARD_BUNDLE_MUTATION,
	TREE_NODE_FRAGMENT,
} from "./create-ihk-certificate-reward-bundle-button.graphql";
import type { CreateIHKCertificateRewardBundleButtonProps } from "./create-ihk-certificate-reward-bundle-button.types";
import { SelectMultipleRootNodesForm } from "../../../../features/educational-offer/select-multiple-root-nodes-form";
import { colorError100Class } from "../../../../themes/color-classes";
import { P2Span } from "../../../../themes/font-tags";
import { useDraftStatusUpdater } from "../use-draft-status-updater";

export const CreateIHKCertificateRewardBundleButton = ({
	disabled,
	treeNodeFragmentRef,
}: CreateIHKCertificateRewardBundleButtonProps) => {
	const { t } = useTranslation("common");
	const setDraft = useDraftStatusUpdater();
	const [selectedRootNodes, setSelectedRootNodes] = useState<string[]>([]);

	const node = useFragment<createIhkCertificateRewardBundleButton_TreeNodeFragment$key>(
		TREE_NODE_FRAGMENT,
		treeNodeFragmentRef,
	);

	const [addIHKCertificateReward, isAddingIHKCertificateReward] =
		useMutation<createIhkCertificateRewardBundleButton_AddIHKCertificateRewardBundleMutation>(
			ADD_IHK_CERTIFICATE_REWARD_BUNDLE_MUTATION,
		);

	const handleOnClick = useCallback(() => {
		addIHKCertificateReward({
			variables: {
				input: {
					contentId: node.id,
					bundleIds: selectedRootNodes,
				},
			},
			updater: setDraft,
		});
	}, [node, addIHKCertificateReward, selectedRootNodes]);

	const handleOnRootNodesSelected = useCallback((rootNodes: string[]) => {
		setSelectedRootNodes(rootNodes);
	}, []);

	const isValid = selectedRootNodes.length > 0;

	const createOnSaveHandler = (hide: () => void) => () => {
		hide();
		handleOnClick();
	};

	return (
		<>
			<DialogButton
				buttonIcon={EpicIcons.SHOPPING_BAG}
				disabled={isAddingIHKCertificateReward || disabled}
				dialogTitle={t("educationalOffer.createIhkBundleCertificateTooltip")}
				dialogFooterNode={(hide) => (
					<>
						<div>
							<Button label={t("actions.cancel")} onClick={hide} />
							<Button
								disabled={!isValid}
								label={t("actions.add")}
								onClick={createOnSaveHandler(hide)}
							/>
						</div>
					</>
				)}
				tooltip={t("educationalOffer.createIhkBundleCertificateTooltip")}
			>
				{() => (
					<>
						<SelectMultipleRootNodesForm
							submitButtonLabel={t("educationalOffer.submitSelectedTrainings")}
							onRootNodesSelected={handleOnRootNodesSelected}
						/>
						{!isValid && (
							<P2Span className={colorError100Class}>
								{t("educationalOffer.selectAtLeastOneTraining")}
							</P2Span>
						)}
					</>
				)}
			</DialogButton>
		</>
	);
};
